export type TranscriptCompletedMessage = {
  itemId: string;
  transcription: string;
  startTime: number;
};

// Words still being recognised: a delta (OpenAI) or the whole partial
// sentence (other providers), keyed by the provider's item id.
export type TranscriptPartialMessage = {
  itemId?: string;
  transcription: string;
};

/**
 * How much of the audio the transcriber received is covered by the final
 * text (newer transcriber versions only). `complete: false` means segments
 * were lost or never finished, so the text must not become the note source.
 */
export type TranscriptCoverage = {
  receivedAudioSeconds: number;
  transcribedAudioSeconds: number;
  missingAudioSeconds: number;
  pendingSegments: number;
  finalization: string;
  complete: boolean;
};

export type TranscriptEndMessage = {
  transcription: string;
  // Uncleaned provider text (newer transcriber versions only).
  rawTranscription?: string;
  postProcessing?: boolean;
  language: string;
  duration: number;
  coverage?: TranscriptCoverage;
};

/**
 * Why live text was dropped in favour of transcribing the full recording:
 * the connection or provider failed, or the live audio missed too much of the
 * recording (for example a background window slowed audio capture).
 */
export type AudioRecoveryReason = "unavailable" | "incomplete";

export type SendChunkMessage = {
  data: string;
};

export interface TranscriptionStreamText {
  text: string;
  startTime: number;
}

export enum NoiseReduction {
  FAR_FIELD = "far_field",
  NEAR_FIELD = "near_field",
}

export enum TranscriptionProviderName {
  OPENAI = "openai",
  SPEECHMATIC = "speechmatic",
  DEEPGRAM = "deepgram",
}

export enum TranscriptionNoRealtimeProviderName {
  OPENAI = "openai/gpt-4o-transcribe",
  DEEPGRAM_NOVA_3_MEDICAL = "deepgram/nova-3-medical",
  DEEPGRAM_NOVA_3 = "deepgram/nova-3",
}

// Retired choices that saved settings may still hold (ElevenLabs Scribe,
// Deepgram Nova-2). Read as Deepgram; never offered.
export const LEGACY_NO_REALTIME_PROVIDERS: Readonly<
  Record<string, TranscriptionNoRealtimeProviderName>
> = {
  "elevenlabs/scribe_v1":
    TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
  "deepgram/nova-2": TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3,
};

export const TranscriptionLanguageWhisperAvailable = [
  { value: "auto", label: "Detect automatically" },
  { value: "eng", label: "English" },
  { value: "spa", label: "Spanish" },
  { value: "fra", label: "French" },
  { value: "por", label: "Portuguese" },
  { value: "jap", label: "Japanese" },
  { value: "cmn", label: "Chinese (Mandarin)" },
];
